import { useState, useRef } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";

const DOCUMENT_TYPES = [
    "Insurance",
    "Fitness Certificate",
    "Pollution Certificate (PUC)",
    "Registration Certificate (RC)",
    "Road Tax",
    "Permit",
    "Road Worthiness Certificate",
    "Driver License",
    "Other",
];

const FIELD_STYLE = {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: "0.95rem",
    color: "#1e293b",
    outline: "none",
    background: "#fff",
    fontFamily: "inherit",
};

const LABEL_STYLE = {
    display: "block",
    fontSize: "1rem",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: 6,
};

export default function AddDocumentModal({ modal, toggle, machineryId, machineryName, assetNo, onSuccess }) {
    const fileRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [form, setForm] = useState({
        documentType: "",
        documentNumber: "",
        issuedBy: "",
        issueDate: "",
        expiryDate: "",
        file: null,
    });

    const reset = () => {
        setForm({ documentType: "", documentNumber: "", issuedBy: "", issueDate: "", expiryDate: "", file: null });
        setDragOver(false);
    };

    const handleClose = () => { reset(); toggle("document"); };

    const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleFile = (file) => {
        if (file) {
            if (file.size > 6 * 1024 * 1024) {
                toast.error("File size must be less than 6MB");
                return;
            }
            setForm((p) => ({ ...p, file }));
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const handleSubmit = () => {
        if (!form.documentType) return toast.error("Please select a document type");
        if (!form.documentNumber) return toast.error("Please enter document number");

        const formData = new FormData();
        formData.append("machineryId", machineryId);
        formData.append("documentType", form.documentType);
        formData.append("documentNumber", form.documentNumber);
        formData.append("issuedBy", form.issuedBy);
        formData.append("issueDate", form.issueDate);
        formData.append("expiryDate", form.expiryDate);
        if (form.file) formData.append("file", form.file);

        setIsLoading(true);
        callApi(apiUrl.add_machinery_document, { method: "POST", form_data: formData }).then((res) => {
            setIsLoading(false);
            if (res?.status === true) {
                toast.success(res?.message || "Document added successfully");
                reset();
                toggle("document");
                onSuccess?.();
            } else {
                toast.error(res?.message || "Failed to add document");
            }
        });
    };

    return (
        <Modal isOpen={modal} toggle={handleClose} centered size="lg">
            <div style={{ borderRadius: 12, overflow: "hidden" }}>
                {/* Header */}
                <div style={{ padding: "24px 28px 16px", borderBottom: "1px solid #f1f5f9" }}>
                    <div className="d-flex align-items-start justify-content-between">
                        <div>
                            <h5 style={{ margin: 0, fontWeight: 800, fontSize: "1.2rem", color: "#0f172a" }}>
                                Add document
                            </h5>
                            {(machineryName || assetNo) && (
                                <p style={{ margin: "4px 0 0", fontSize: "0.88rem", color: "#64748b" }}>
                                    {[machineryName, assetNo].filter(Boolean).join(" · ")}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={handleClose}
                            style={{ background: "none", border: "none", fontSize: "1.3rem", color: "#94a3b8", cursor: "pointer", lineHeight: 1, padding: 0 }}
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: "24px 28px" }}>
                    {/* Row 1: Document Type + Number */}
                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <label style={LABEL_STYLE}>Document Type</label>
                            <select
                                name="documentType"
                                value={form.documentType}
                                onChange={handleChange}
                                style={{ ...FIELD_STYLE, cursor: "pointer" }}
                            >
                                <option value="">Select type…</option>
                                {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="col-6">
                            <label style={LABEL_STYLE}>Document Number</label>
                            <input
                                type="text"
                                name="documentNumber"
                                value={form.documentNumber}
                                onChange={handleChange}
                                placeholder="e.g. INS-4412-27"
                                style={FIELD_STYLE}
                            />
                        </div>
                    </div>

                    {/* Row 2: Issued By */}
                    <div className="mb-3">
                        <label style={LABEL_STYLE}>Issued By</label>
                        <input
                            type="text"
                            name="issuedBy"
                            value={form.issuedBy}
                            onChange={handleChange}
                            placeholder="e.g. ICICI Lombard, RTO Jodhpur"
                            style={FIELD_STYLE}
                        />
                    </div>

                    {/* Row 3: Issue Date + Expiry Date */}
                    <div className="row g-3 mb-3">
                        <div className="col-6">
                            <label style={LABEL_STYLE}>Issue Date</label>
                            <input
                                type="date"
                                name="issueDate"
                                value={form.issueDate}
                                onChange={handleChange}
                                style={FIELD_STYLE}
                            />
                        </div>
                        <div className="col-6">
                            <label style={LABEL_STYLE}>Expiry Date</label>
                            <input
                                type="date"
                                name="expiryDate"
                                value={form.expiryDate}
                                onChange={handleChange}
                                style={FIELD_STYLE}
                            />
                            <p style={{ margin: "5px 0 0", fontSize: "0.8rem", color: "#94a3b8" }}>
                                Drives the expiry alert
                            </p>
                        </div>
                    </div>

                    {/* Row 4: File Upload */}
                    <div>
                        <label style={LABEL_STYLE}>Upload File</label>
                        <div
                            onClick={() => fileRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            style={{
                                border: `2px dashed ${dragOver ? "#4f46e5" : "#d1d5db"}`,
                                borderRadius: 10,
                                background: dragOver ? "#f5f3ff" : "#f8fafc",
                                padding: "28px 20px",
                                textAlign: "center",
                                cursor: "pointer",
                                transition: "all 0.15s",
                            }}
                        >
                            {form.file ? (
                                <div>
                                    <span style={{ fontSize: "1.4rem" }}>📄</span>
                                    <p style={{ margin: "8px 0 0", fontSize: "0.92rem", color: "#4f46e5", fontWeight: 600 }}>
                                        {form.file.name}
                                    </p>
                                    <p style={{ margin: "2px 0 0", fontSize: "0.8rem", color: "#94a3b8" }}>
                                        {(form.file.size / 1024).toFixed(1)} KB — click to change
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <p style={{ margin: 0, fontSize: "0.95rem", color: "#94a3b8" }}>
                                        Drop a file here or click to browse
                                    </p>
                                </div>
                            )}
                        </div>
                        <p style={{ margin: "6px 0 0", fontSize: "0.8rem", color: "#94a3b8" }}>
                            PDF or image — stored against the asset
                        </p>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            style={{ display: "none" }}
                            onChange={(e) => handleFile(e.target.files[0])}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: "16px 28px",
                    borderTop: "1px solid #f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 10,
                }}>
                    <button
                        onClick={handleClose}
                        style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            padding: "9px 22px",
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            color: "#334155",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading || !form.documentType || !form.documentNumber}
                        style={{
                            background: (isLoading || !form.documentType || !form.documentNumber) ? "#94a3b8" : "#1e293b",
                            border: "none",
                            borderRadius: 8,
                            padding: "9px 22px",
                            fontSize: "0.95rem",
                            fontWeight: 600,
                            color: "#fff",
                            cursor: (isLoading || !form.documentType || !form.documentNumber) ? "not-allowed" : "pointer",
                        }}
                    >
                        {isLoading ? "Saving…" : "Add document"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
